import React from "react";
import { cn } from "@/shared/utils/cn";

type TimeInputWithPicker = HTMLInputElement & {
  showPicker?: () => void;
};

export interface NativeTimeInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Thin wrapper around a native time input.
 * Mobile browsers/webviews can show a platform clock picker for this field.
 */
export function NativeTimeInput({
  value,
  onChange,
  className,
  ...props
}: NativeTimeInputProps) {
  const openPicker = (element: EventTarget | null) => {
    const input = element as TimeInputWithPicker | null;
    if (!input?.showPicker) return;
    try {
      input.showPicker();
    } catch {
      // Some environments can throw if picker can't be opened; no-op fallback.
    }
  };

  return (
    <input
      {...props}
      type="time"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onClick={(event) => {
        props.onClick?.(event);
        openPicker(event.currentTarget);
      }}
      onFocus={(event) => {
        props.onFocus?.(event);
        openPicker(event.currentTarget);
      }}
      className={cn(
        "w-full rounded-xl border-2 border-teal-400 bg-white px-4 py-4 text-2xl font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 [color-scheme:light]",
        className
      )}
    />
  );
}
